import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { FAQ_ITEMS } from "@/lib/faq";

/**
 * Accordion-style FAQ list. First question is open by default; clicking any
 * question toggles it. Multiple can be open simultaneously so the user can
 * scan freely.
 *
 * The JSON-LD FAQPage schema is rendered separately by the parent via
 * SeoHead.schema — search engines pick the full list up regardless of which
 * items are visually open here.
 */
export const FaqAccordion = ({ items = FAQ_ITEMS, defaultOpenIndex = 0 }) => {
  const [openSet, setOpenSet] = useState(() => new Set([defaultOpenIndex]));

  const toggle = (i) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  return (
    <ul className="space-y-3" data-testid="faq-accordion">
      {items.map((item, i) => {
        const open = openSet.has(i);
        const panelId = `faq-panel-${i}`;
        const btnId = `faq-btn-${i}`;
        return (
          <li
            key={i}
            className="border-2 border-[#1f1f1f] transition-colors"
            style={{ background: open ? "#FFF7E6" : "#FDFBF7" }}
            data-testid={`faq-item-${i}`}
            data-open={open ? "true" : "false"}
          >
            <button
              type="button"
              id={btnId}
              onClick={() => toggle(i)}
              aria-expanded={open}
              aria-controls={panelId}
              className="w-full flex items-center justify-between gap-3 px-4 md:px-5 py-4 text-left hover:opacity-95"
              data-testid={`faq-question-${i}`}
            >
              <span className="flex items-baseline gap-3 min-w-0">
                <span className="text-[10px] uppercase font-extrabold tracking-[0.3em] shrink-0" style={{ color: "#FF9933" }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-display uppercase text-base md:text-lg leading-tight tracking-tight">
                  {item.q}
                </span>
              </span>
              <ChevronDown
                size={18}
                strokeWidth={3}
                className="shrink-0 transition-transform"
                style={{ transform: open ? "rotate(180deg)" : "none" }}
              />
            </button>

            {open && (
              <div
                id={panelId}
                role="region"
                aria-labelledby={btnId}
                className="px-4 md:px-5 pb-5 -mt-1 border-t border-[#1f1f1f]/20 pt-4"
                data-testid={`faq-answer-${i}`}
              >
                {item.a.split("\n\n").map((para, j) => (
                  <p key={j} className="text-sm md:text-base font-medium leading-relaxed mb-3 last:mb-0">
                    {para}
                  </p>
                ))}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default FaqAccordion;
